import { Link } from "react-router-dom";
import { Instagram, Youtube, Mail, Phone, MapPin } from "lucide-react";
import { useSettings } from "@/lib/settings";
import LogoMark from "@/components/site/LogoMark";

export default function Footer() {
  const { settings } = useSettings();
  const f = settings?.footer || { columns: [] };
  const c = settings?.contact || {};
  const siteName = settings?.site_name || "Happy Retreats";

  return (
    <footer id="contact" className="bg-ink text-ivory pt-24 pb-10" data-testid="site-footer">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            {settings?.logo_url_dark ? (
              <img src={settings.logo_url_dark} alt={siteName} className="h-16 md:h-20 w-auto" />
            ) : settings?.logo_url ? (
              <LogoMark src={settings.logo_url} dark className="h-16 md:h-20 w-auto" alt={siteName} />
            ) : null}
            {!settings?.logo_url && (
              <div className="font-display text-4xl md:text-5xl tracking-tight leading-tight">
                {siteName.split(" ")[0]} <span className="italic text-clay">{siteName.split(" ").slice(1).join(" ")}</span>
              </div>
            )}
            <p className="mt-6 max-w-md text-ivory/70 text-base leading-relaxed font-light">
              {f.tagline}
            </p>
            <div className="mt-10 space-y-4 text-sm text-ivory/80">
              {c.phone && (
                <a href={`tel:${c.phone}`} className="flex items-center gap-3 hover:text-clay" data-testid="footer-phone">
                  <Phone className="w-4 h-4" /> {c.phone}
                </a>
              )}
              {c.email && (
                <a href={`mailto:${c.email}`} className="flex items-center gap-3 hover:text-clay" data-testid="footer-email">
                  <Mail className="w-4 h-4" /> {c.email}
                </a>
              )}
              {c.address && (
                <div className="flex items-center gap-3"><MapPin className="w-4 h-4" /> {c.address}</div>
              )}
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-10">
            {(f.columns || []).map((col, i) => (
              <div key={i}>
                <div className="overline text-ivory/60 mb-5">{col.title}</div>
                <ul className="space-y-3">
                  {(col.links || []).map((l, j) => (
                    <li key={j}>
                      <Link to={l.url} className="text-sm text-ivory/85 hover:text-clay transition-colors" data-testid={`footer-link-${i}-${j}`}>
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="hairline mt-16 bg-ivory/20" />
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pt-8">
          <div className="text-xs text-ivory/60">{f.copyright}</div>
          <div className="flex items-center gap-5">
            {(f.social_links || []).map((s, i) => {
              const Icon = s.platform === "Instagram" ? Instagram : s.platform === "YouTube" ? Youtube : Mail;
              return (
                <a key={i} href={s.url} target="_blank" rel="noreferrer" className="text-ivory/70 hover:text-clay" data-testid={`footer-social-${i}`}>
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
}
